/**
 * mm-cart-discount.js
 * Handles discount code application and removal in the cart drawer.
 *
 * Strategy (aligned with Shopify's modern cart-discount component):
 *  - Apply  → single POST /cart/update.js { discount: "CODE1,CODE2,...", sections:[id] }
 *             Shopify's cart JSON always returns `discount_codes: [{code, applicable}]`,
 *             which tells us directly whether the code we just tried is valid.
 *  - Remove → single POST /cart/update.js { discount: "<codes minus the removed one>" }
 *
 * Why we dropped the old GET /discount/{CODE} + /cart.js verification approach:
 *   - It relied on a redirect + session cookie round trip that could silently fail
 *     or desync from the actual cart discount state.
 *   - Removing a code required clearing everything and replaying every surviving
 *     code sequentially (GET then POST, one at a time) — slow and error prone.
 *   - The "shipping discount" error path was declared but never actually reached,
 *     since _applyDiscount() never returned { type: 'shipping' }.
 *
 * The custom element <cart-discount-component> is defined here and wired to:
 *   form submit                        → _handleApply()
 *   .cart-discount__pill-remove click  → _handleRemove()  (event-delegated on document)
 *
 * After any successful change we still lean on the theme's existing
 * mmOpenAndRefreshCart()/mmRefreshCart() (from mm_cart.js) to re-render the
 * drawer, so cross-sell sliders, quantity widgets, etc. keep being
 * re-initialised exactly the way the rest of the site already expects.
 */

class CartDiscountComponent extends HTMLElement {
  constructor() {
    super();

    // ── DOM refs ────────────────────────────────────────────────────────────
    this._form         = this.querySelector('.cart-discount__form');
    this._input        = this.querySelector('#cart-discount');
    this._errorBox     = this.querySelector('[ref="cartDiscountError"]');
    this._errCode      = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
    this._errShipping  = this.querySelector('[ref="cartDiscountErrorShipping"]');
    this._errActive    = this.querySelector('[ref="cartDiscountErrorAlreadyActive"]');
    this._codesList    = this.closest('.details-content')
                           ?.closest('accordion-custom')
                           ?.querySelector('.cart-discount__codes')
                         ?? document.querySelector('.cart-discount__codes');

    // Section that renders this component, used to ask Shopify to also
    // return the freshly rendered markup so we can detect edge cases
    // (e.g. a shipping-only discount) before deciding how to refresh the UI.
    this._sectionId = this.dataset.sectionId
      || this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')
      || null;

    this._busy = false; // prevents double-submit
    this._abortController = null;
  }

  connectedCallback() {
    // Apply discount on form submit
    this._form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._handleApply();
    });

    // Remove discount when a pill's × button is clicked
    // We delegate from the document so it works after re-renders
    // (the list itself may be replaced by Liquid on refresh).
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-discount__pill-remove');
      if (!btn) return;
      const pill = btn.closest('.cart-discount__pill');
      if (!pill) return;
      const code = pill.dataset.discountCode;
      if (code) this._handleRemove(code);
    });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Returns the list of currently active discount codes
   * by reading the rendered pills in the DOM.
   */
  _getActiveCodes() {
    const pills = document.querySelectorAll('.cart-discount__pill[data-discount-code]');
    return Array.from(pills).map((p) => p.dataset.discountCode.toUpperCase());
  }

  /**
   * Show/hide error messages.
   * @param {'code'|'shipping'|'active'|null} type  null = hide all
   * @param {string} [code]  the discount code, used to interpolate the message
   */
  _showError(type, code = '') {
    // Hide everything first
    this._errorBox?.classList.add('hidden');
    this._errCode?.classList.add('hidden');
    this._errShipping?.classList.add('hidden');
    this._errActive?.classList.add('hidden');

    if (!type) return;

    this._errorBox?.classList.remove('hidden');

    if (type === 'code') {
      // Interpolate the code into the error message (matches Liquid t filter pattern)
      if (this._errCode) {
        const raw = this._errCode.dataset.rawMessage ?? this._errCode.textContent;
        if (!this._errCode.dataset.rawMessage) {
          this._errCode.dataset.rawMessage = raw; // cache original
        }
        this._errCode.textContent = raw.replace('test', code); // 'test' is the placeholder used in Liquid
        this._errCode.classList.remove('hidden');
      }
    } else if (type === 'shipping') {
      this._errShipping?.classList.remove('hidden');
    } else if (type === 'active') {
      this._errActive?.classList.remove('hidden');
    }
  }

  /**
   * Set loading state on the submit button.
   */
  _setLoading(isLoading) {
    const btn = this._form?.querySelector('.cart-discount__button');
    if (!btn) return;
    btn.disabled = isLoading;
    btn.style.opacity = isLoading ? '0.6' : '';
    btn.style.cursor  = isLoading ? 'wait' : '';
  }

  /**
   * Refresh the cart drawer after a discount change.
   * Relies on mmOpenAndRefreshCart() or mmRefreshCart() from mm_cart.js.
   */
  _refreshCart(keepOpen = true) {
    if (keepOpen && typeof mmOpenAndRefreshCart === 'function') {
      mmOpenAndRefreshCart();
    } else if (typeof mmRefreshCart === 'function') {
      mmRefreshCart();
    }
  }

  /**
   * Aborts any in-flight discount request and returns a fresh AbortController.
   */
  _createAbortController() {
    this._abortController?.abort();
    this._abortController = new AbortController();
    return this._abortController;
  }

  // ── Apply ──────────────────────────────────────────────────────────────────

  async _handleApply() {
    if (this._busy) return;

    const code = (this._input?.value ?? '').trim().toUpperCase();
    if (!code) return;

    // Guard: already active?
    const activeCodes = this._getActiveCodes();
    if (activeCodes.includes(code)) {
      this._showError('active', code);
      return;
    }

    this._showError(null);
    this._busy = true;
    this._setLoading(true);

    try {
      const result = await this._applyDiscount(code, activeCodes);

      if (result.valid === false) {
        // Shopify rejected the code (invalid / not applicable to this cart)
        this._showError('code', code);
      } else if (result.type === 'shipping') {
        // Shipping-only discounts are accepted server-side but can't be
        // shown as a pill in the drawer — inform the user and leave the
        // drawer state untouched (the discount is still attached to the cart).
        this._showError('shipping', code);
        if (this._input) this._input.value = '';
      } else {
        // Success
        this._showError(null);
        if (this._input) this._input.value = '';
        this._refreshCart(true);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[mm-cart-discount] Apply error:', err);
      this._showError('code', code);
    } finally {
      this._busy = false;
      this._setLoading(false);
    }
  }

  /**
   * Applies a discount code by sending the full, updated list of codes to
   * /cart/update.js in a single request (Shopify supports comma-separated
   * discount codes when "combine discounts" is enabled for the shop).
   *
   * Returns:
   *   { valid: true }                 → applied and visible as a pill
   *   { valid: true, type: 'shipping' } → applied, but shipping-only (no pill)
   *   { valid: false }                → rejected by Shopify
   */
  async _applyDiscount(code, existingCodes) {
    const lang = this._getLangPrefix();
    const codesToApply = [...existingCodes, code];
    const abortController = this._createAbortController();

    const response = await fetch(`${lang}/cart/update.js`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: codesToApply.join(','),
        sections: this._sectionId ? [this._sectionId] : undefined,
      }),
      signal: abortController.signal,
    });

    const data = await response.json();

    // 1. Did Shopify explicitly mark our code as not applicable?
    const rejected = (data.discount_codes || []).some(
      (d) => (d.code || '').toUpperCase() === code && d.applicable === false
    );
    if (rejected) {
      return { valid: false };
    }

    // 2. Detect shipping-only discounts: Shopify marks the code as
    //    applicable, but the section's re-rendered pill list doesn't
    //    contain it (mirrors the Liquid logic used to build `discount_codes`
    //    in the "discount_location" block of this section).
    if (this._sectionId && data.sections?.[this._sectionId]) {
      const parsedHtml = new DOMParser().parseFromString(data.sections[this._sectionId], 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this._sectionId}`);
      const renderedPills = section?.querySelectorAll('.cart-discount__pill[data-discount-code]') || [];
      const renderedCodes = Array.from(renderedPills).map((el) => (el.dataset.discountCode || '').toUpperCase());

      const isApplicable = (data.discount_codes || []).some(
        (d) => (d.code || '').toUpperCase() === code && d.applicable === true
      );

      if (isApplicable && !renderedCodes.includes(code)) {
        return { valid: true, type: 'shipping' };
      }
    }

    return { valid: true };
  }

  // ── Remove ─────────────────────────────────────────────────────────────────

  async _handleRemove(code) {
    if (this._busy) return;

    this._showError(null);
    this._busy = true;

    // Optimistically hide the pill immediately for snappy UX
    const pill = document.querySelector(`.cart-discount__pill[data-discount-code="${code}"]`);
    if (pill) pill.style.opacity = '0.4';

    try {
      await this._removeDiscount(code);
      this._refreshCart(true);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[mm-cart-discount] Remove error:', err);
      if (pill) pill.style.opacity = ''; // restore on failure
    } finally {
      this._busy = false;
    }
  }

  /**
   * Removes a discount code by sending the remaining codes (minus the one
   * being removed) to /cart/update.js in a single request.
   */
  async _removeDiscount(codeToRemove) {
    const lang = this._getLangPrefix();
    const survivors = this._getActiveCodes().filter((c) => c !== codeToRemove.toUpperCase());
    const abortController = this._createAbortController();

    await fetch(`${lang}/cart/update.js`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: survivors.join(',') }),
      signal: abortController.signal,
    });
  }

  /**
   * Returns the current language/root path prefix (e.g. "/en", "/fr", "").
   *
   * We no longer try to infer this from window.location.pathname or from
   * getLangPath() (mm_cart.js): both approaches ended up treating the
   * first real path segment (/products, /pages, ...) as if it were a
   * locale, producing broken requests like /products/cart/update.js or
   * /pages/cart/update.js.
   *
   * Shopify.routes.root is provided natively by Shopify on every page
   * (set in theme.liquid, usually via `{{ routes.root_url | json }}`)
   * and is always correct — it's "/" on a single-locale store, or
   * "/en/" etc. when the storefront has locale-prefixed URLs.
   */
  _getLangPrefix() {
    const root = window.Shopify?.routes?.root;
    if (typeof root === 'string') {
      // Normalise "/" or "" to "" so we can safely do `${prefix}/cart/update.js`
      return root === '/' ? '' : root.replace(/\/$/, '');
    }

    // Fallback if Shopify.routes.root isn't available for some reason:
    // only treat the first path segment as a locale if it's actually a
    // known locale code — never trust it blindly.
    const locales = ['fr', 'en', 'es', 'de']; // à enrichir en cas de nouvelle langue
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts.length > 0 && locales.includes(parts[0])) {
      return '/' + parts[0];
    }
    return '';
  }
}

// ── Register custom element ──────────────────────────────────────────────────
if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscountComponent);
}